<template>
  <ContextColorProvider context="wealth">
    <NavBar>
      <template #left>
        <BackButton @click="handleGoBackClick" />
      </template>
      <span
        slot="center"
        class="wrn-color-black"
        data-testId="risk-level-title"
      >
        Proventos
      </span>
      <template #right>
        <ShowMoneyToggleButton />
      </template>
    </NavBar>
    <main class="profit__main">
      <div>
        <MainLoading v-if="isLoading" />
        <MainError
          v-else-if="hasError"
          title="Não foi possivel carregar os detalhes proventos"
          description="Aguarde alguns minutos e tente novamente."
          @on-retry="getProfitData"
        />
        <div v-else>
          <Heading :level="2" class="profit__main__heading">
            {{ profitData?.label }}
          </Heading>
          <HeadingDisplay
            size="extra-small"
            head="Proventos recebidos"
            title=""
          >
            <template #title-component>
              <Money>{{ amount }}</Money>
            </template>
          </HeadingDisplay>

          <DataTable
            show-divider
            has-interaction
            class="profit__main__table"
            data-testid="profit-table"
            :status="tableStatus"
            :columns="columns"
            :data="profitData?.stockEvents"
            @on-row-click="onRowClick"
          >
            <template #error>
              <HelperViewStatus
                class="orders-table__helper"
                status="alert"
                button-label="Tentar novamente"
                title="Desculpe, não conseguimos carregar os proventos recebidos"
                description="Aguarde um momento e tente novamente"
                @click="getProfitData"
              />
            </template>
          </DataTable>
        </div>
        <StockProfitModal
          :is-open="isModalOpen"
          :portfolio-id="portfolioId"
          :ticker="rowTicker"
          :year="year"
          :month="month"
          @on-close="toggleModalOpen"
        />
      </div>
    </main>
  </ContextColorProvider>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { WarrenSession } from '@/modules/common/services/session';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  ContextColorProvider,
  HelperViewStatus,
  DataTable,
  Heading,
  HeadingDisplay
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { ShowMoneyToggleButton } from '@/modules/common/components/buttons/ShowMoneyToggleButton';

import {
  getProfitDetail,
  ProfitDetailResponse,
  StockEvent
} from '@/modules/wealth/services/portfolio';
import { columns } from './columns';
import { Money } from '@/modules/common/components/MoneyV2';
import { MainError } from '@/modules/trade/components/MainError';
import { MainLoading } from '@/modules/trade/components/MainLoading';
import { StockProfitModal } from './components/StockProfitModal';
import { StorageKeys } from '@/modules/common/services/local-storage';

const profitData = ref<ProfitDetailResponse | null>(null);
const isLoading = ref(false);
const hasError = ref(false);
const isModalOpen = ref(false);
const rowTicker = ref('');

const route = useRoute();
const router = useRouter();

const toggleModalOpen = () => {
  isModalOpen.value = !isModalOpen.value;
};

const portfolioId = computed(() => {
  const { portfolioId } = route.params;
  return portfolioId;
});

const query = computed(() => {
  const { year, month } = route.params;
  return { year, month };
});

const amount = computed(() => {
  return WarrenSession.get(StorageKeys.ProfitMonthlyAmount) || '-';
});

const handleGoBackClick = () => {
  WarrenSession.set('warren.indexPortfolio.filterOption', 'income');
  router.replace({
    name: 'wealthIndexPortfolio',
    params: { portfolioId: portfolioId.value }
  });
};

const tableStatus = computed(() => {
  if (hasError.value) return 'error';
  if (isLoading.value) return 'loading';

  return 'default';
});

const month = computed(() => {
  const { month } = query.value;

  return month;
});

const year = computed(() => {
  const { year } = query.value;

  return year;
});

const getProfitData = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;

    profitData.value = await getProfitDetail(
      portfolioId.value,
      year.value,
      month.value
    );
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const onRowClick = (row: StockEvent) => {
  rowTicker.value = row.name;
  isModalOpen.value = true;
};

onMounted(getProfitData);
</script>

<style lang="less" scoped>
:deep(.table) {
  table-layout: fixed;
}
.profit {
  &__main {
    max-width: 1040px;
    margin: 0 auto;
    padding: @size-spacing-x1200 @size-spacing-x400;

    &__money-cell {
      font-size: @size-spacing-x600;
      @media screen and (max-width: 640px) {
        font-size: @size-spacing-x500;
      }
    }

    &__error {
      margin: @size-spacing-x1800 auto 0 auto;
    }

    &__table {
      margin-top: @size-spacing-x500;
    }

    &__heading {
      padding-bottom: @size-spacing-x500;
    }
  }
}
</style>
